import Hotjar from '@hotjar/browser'
import { ILanguage } from '../interfaces/ILanguage'


export type NormalizedError = {
   status?: number
   titleKey: keyof ILanguage
   messageKey: keyof ILanguage
   traceId?: string
   detail?: string
   isUnauthorized: boolean
}

export const showTechnicalDetails =
   process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'test'

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
   typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined

const nestedRecords = (record: Record<string, unknown>): Record<string, unknown>[] =>
   [record.error, record.response, record.data].map(asRecord).filter((x): x is Record<string, unknown> => x !== undefined)

const getStatus = (error: unknown): number | undefined => {
   if (typeof Response !== 'undefined' && error instanceof Response) return error.status
   const record = asRecord(error)
   if (!record) return undefined
   if (typeof record.status === 'number') return record.status
   for (const nested of nestedRecords(record)) {
      if (typeof nested.status === 'number') return nested.status
   }
   return undefined
}

const getTraceId = (error: unknown): string | undefined => {
   const record = asRecord(error)
   if (!record) return undefined
   if (typeof record.traceId === 'string') return record.traceId
   for (const nested of nestedRecords(record)) {
      if (typeof nested.traceId === 'string') return nested.traceId
   }
   return undefined
}

const getDetail = (error: unknown): string | undefined => {
   if (typeof error === 'string') return error
   if (error instanceof Error) return error.stack ?? error.message
   const record = asRecord(error)
   if (!record) return undefined

   const texts = [record.title, record.detail, record.message, record.statusText].filter((x): x is string => typeof x === 'string')
   if (texts.length > 0) return texts.join(' - ')

   try {
      return JSON.stringify(error)
   } catch {
      return undefined
   }
}

const isNetworkError = (error: unknown): boolean => {
   if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
   if (!(error instanceof TypeError)) return false
   return /failed to fetch|networkerror|network request failed|load failed/i.test(error.message)
}

const messageKeyForStatus = (status: number): keyof ILanguage => {
   // status 0 = answer never reached (CORS, blocked by adblock, interrupted connection, etc.)
   if (status === 0) return 'ErrorNetworkMessage'
   if (status === 400 || status === 422) return 'ErrorBadRequestMessage'
   if (status === 401) return 'ErrorSessionExpiredMessage'
   if (status === 403) return 'ErrorForbiddenMessage'
   if (status === 404) return 'ErrorNotFoundMessage'
   if (status === 409) return 'ErrorConflictMessage'
   if (status === 408 || status === 504) return 'ErrorTimeoutMessage'
   if (status >= 500) return 'ErrorServerMessage'
   return 'ErrorUnexpectedMessage'
}

const titleKeyForStatus = (status?: number): keyof ILanguage => {
   if (status === 403) return 'NotAuthorized'
   if (status === 404) return 'ErrorNotFoundTitle'
   return 'SomethingWentWrong'
}

const readError = (error: unknown): NormalizedError => {
   const status = getStatus(error)
   const isUnauthorized = status === 401 || error === 'Unauthorized'

   let messageKey: keyof ILanguage
   if (isUnauthorized) messageKey = 'ErrorSessionExpiredMessage'
   else if (status !== undefined) messageKey = messageKeyForStatus(status)
   else if (isNetworkError(error)) messageKey = 'ErrorNetworkMessage'
   else messageKey = 'ErrorUnexpectedMessage'

   return {
      status,
      titleKey: titleKeyForStatus(status),
      messageKey,
      traceId: getTraceId(error),
      detail: getDetail(error),
      isUnauthorized,
   }
}

export const normalizeError = (error: unknown): NormalizedError => {
   try {
      return readError(error)
   } catch {
      return { titleKey: 'SomethingWentWrong', messageKey: 'ErrorUnexpectedMessage', isUnauthorized: false }
   }
}

export const logAppError = (error: unknown, componentStack?: string): void => {
   // eslint-disable-next-line no-console
   console.error('[AppError]', error, componentStack ?? '')

   if (process.env.REACT_APP_HOTJAR_SITE_ID) {
      try {
         Hotjar.event('app_error')
      } catch {
         // Logging error to Hotjar failed, but we don't want to crash the app because of that
      }
   }
}
